import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { SellerAccountFulfillmentRulesClient } from 'src/WebApiController';

export const useSellerAccountEventFulfillmentRules = (
  eventId?: number | null,
  eventMappingId?: string | null,
  existingRuleId?: number | null,
  disabled?: boolean
) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();
  const queryClient = useQueryClient();

  const queryKey = [
    'SellerAccountFulfillmentRulesClient.getFulfillmentRuleForEvent',
    activeAccountWebClientConfig.activeAccountId,
  ];

  const shouldQuery =
    !disabled && activeAccountWebClientConfig.activeAccountId != null;
  const eventFulfillmentRuleQuery = useQuery({
    queryKey,
    queryFn: async () => {
      if (!shouldQuery) return null;
      return await new SellerAccountFulfillmentRulesClient(
        activeAccountWebClientConfig
      ).getFulfillmentRuleForEvent(eventId, eventMappingId);
    },
    enabled: shouldQuery,
    refetchOnWindowFocus: false,
    meta: {
      onError: (error: ErrorTypes) => {
        showErrorDialog(
          'SellerAccountFulfillmentRulesClient.getFulfillmentRuleForEvent',
          error,
          {
            trackErrorData: {
              activeAccountId: activeAccountWebClientConfig.activeAccountId,
              eventId,
              eventMappingId,
            },
          }
        );
      },
    },
  });

  const createMutation = useMutation({
    mutationFn: async (fulfillerSellerUserId: string) => {
      if (!eventId && !eventMappingId) {
        throw new Error('Event ID or Mapping ID is required');
      }

      const client = new SellerAccountFulfillmentRulesClient(
        activeAccountWebClientConfig
      );
      return await client.createFulfillmentRuleForEvent({
        sellerAccountFulfillmentRulesId: null,
        viagogoEventId: eventId ?? null,
        eventMappingId: eventMappingId ?? null,
        fulfillerSellerUserId,
        fulfillmentRuleTargetTypeId: 1,
        purchaseOrderId: null,
        purchaseOrderVendorId: null,
        viagogoVenueId: null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (error: ErrorTypes) => {
      showErrorDialog(
        'SellerAccountFulfillmentRulesClient.createFulfillmentRuleForEvent',
        error,
        {
          trackErrorData: {
            activeAccountId: activeAccountWebClientConfig.activeAccountId,
            eventId,
            eventMappingId,
          },
        }
      );
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      ruleId,
      fulfillerSellerUserId,
    }: {
      ruleId: number;
      fulfillerSellerUserId: string;
    }) => {
      if (!eventId && !eventMappingId) {
        throw new Error('Event ID or Mapping ID is required');
      }

      const client = new SellerAccountFulfillmentRulesClient(
        activeAccountWebClientConfig
      );
      return await client.updateFulfillmentRuleForEvent({
        sellerAccountFulfillmentRulesId: ruleId,
        viagogoEventId: eventId ?? null,
        eventMappingId: eventMappingId ?? null,
        fulfillerSellerUserId,
        fulfillmentRuleTargetTypeId: 1,
        purchaseOrderId: null,
        purchaseOrderVendorId: null,
        viagogoVenueId: null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (error: ErrorTypes) => {
      showErrorDialog(
        'SellerAccountFulfillmentRulesClient.updateFulfillmentRuleForEvent',
        error,
        {
          trackErrorData: {
            activeAccountId: activeAccountWebClientConfig.activeAccountId,
            eventId,
            eventMappingId,
            ruleId: existingRuleId,
          },
        }
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!eventId && !eventMappingId) {
        throw new Error('Event ID or Mapping ID is required');
      }

      const client = new SellerAccountFulfillmentRulesClient(
        activeAccountWebClientConfig
      );
      if (existingRuleId) {
        return await client.deleteFulfillmentRule(existingRuleId);
      }
      return { success: true };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (error: ErrorTypes) => {
      showErrorDialog(
        'SellerAccountFulfillmentRulesClient.deleteFulfillmentRule',
        error,
        {
          trackErrorData: {
            activeAccountId: activeAccountWebClientConfig.activeAccountId,
            eventId,
            eventMappingId,
          },
        }
      );
    },
  });

  const handleSaveChanges = (fulfillerSellerUserId: string) => {
    if (existingRuleId) {
      updateMutation.mutate({ ruleId: existingRuleId, fulfillerSellerUserId });
    } else {
      createMutation.mutate(fulfillerSellerUserId);
    }
  };

  const handleResetSetting = () => {
    deleteMutation.mutate();
  };

  return {
    eventFulfillmentRuleQuery,
    handleSaveChanges,
    handleResetSetting,
    isMutating:
      createMutation.isPending ||
      updateMutation.isPending ||
      deleteMutation.isPending,
  };
};
